// ─── Impostazioni → Campi ────────────────────────────────────────────────────
//
// 🎯 Il primo pezzo del circolo che si descrive da sé. Finché i campi arrivano
// da fuori, il giorno in cui quel «fuori» si spegne la griglia resta vuota.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Campo as CampoCircolo } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'campi';

export function Campi(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const elenco = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<{ items: CampoCircolo[] }>('GET', '/settings/courts'),
  });

  if (elenco.isLoading) return <Attesa cosa="i campi" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  const items = elenco.data?.items ?? [];

  return (
    <section>
      <header className="testata">
        <h2>Campi</h2>
        <p className="tenue">
          {items.filter((c) => c.active).length} in servizio su {items.length}.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      {items.length === 0 ? (
        <Vuoto
          titolo="Nessun campo"
          perché="Senza campi il calendario non ha colonne: qui sotto se ne apre uno."
        />
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ordine</th>
                <th>Superficie</th>
                <th>Coperto</th>
                <th>Stato</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <Riga key={c.id} campo={c} modificabile={modificabile} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modificabile && <Nuovo />}
    </section>
  );
}

function Riga({ campo, modificabile }: { campo: CampoCircolo; modificabile: boolean }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  /**
   * 🗣️ *«mi manca sulla riga un modifica, se voglio modificare qualcosa dentro
   * un campo»*. Il server accettava già nome, ordine, superficie e coperto
   * (`modificaCampoSchema`): mancava solo il modo di chiederglielo.
   * ⚠️ Il pannello si apre in una RIGA SUA sotto, non dentro una cella: un
   *    modulo dentro `<td>` allargherebbe la colonna e sfascerebbe la tabella.
   */
  const [aperto, setAperto] = useState(false);
  const cambia = useMutation({
    meta: {
      fatto: (_d, c) => {
        const v = c as { active?: boolean; sortOrder?: number };
        if (v.active === false) return `${campo.name} disattivato`;
        if (v.active === true) return `${campo.name} riattivato`;
        return 'ordine dei campi aggiornato';
      },
    },
    mutationFn: (c: { active?: boolean; sortOrder?: number }) =>
      chiama('PATCH', `/settings/courts/${campo.id}`, c),
    onSuccess: rileggi,
  });
  const elimina = useMutation({
    meta: { fatto: `${campo.name} eliminato` },
    mutationFn: () => chiama('DELETE', `/settings/courts/${campo.id}`),
    onSuccess: rileggi,
  });

  return (
    <>
      <tr className={campo.active ? '' : 'spento'}>
        <td>{campo.name}</td>
        <td>{campo.sortOrder}</td>
        <td>{campo.surface ?? '—'}</td>
        <td>{campo.indoor === null ? '—' : campo.indoor ? 'sì' : 'no'}</td>
        <td>{campo.active ? 'in servizio' : 'spento'}</td>
        <td className="azioni">
          {modificabile && (
            <>
              <button
                type="button"
                className="piatto"
                aria-expanded={aperto}
                onClick={() => setAperto((v) => !v)}
              >
                Modifica
              </button>
              <button
                type="button"
                className="piatto"
                disabled={cambia.isPending}
                onClick={() => cambia.mutate({ active: !campo.active })}
              >
                {campo.active ? 'Spegni' : 'Riaccendi'}
              </button>
              {/* 🚨 Cancellare è un'altra cosa da spegnere, e il server lo rifiuta
                  se il campo ha una storia: le sue prenotazioni dicono chi ha
                  giocato e quanto ha pagato. */}
              <button
                type="button"
                className="piatto pericolo"
                disabled={elimina.isPending}
                onClick={() => elimina.mutate()}
              >
                Elimina
              </button>
            </>
          )}
          <Guasto errore={cambia.error ?? elimina.error} />
        </td>
      </tr>
      {modificabile && aperto && (
        <tr className="riga-modifica">
          <td colSpan={6}>
            <Modifica campo={campo} chiudi={() => setAperto(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Cambiare un campo già aperto.
 *
 * ⭐ Si manda SOLO quello che è cambiato davvero: `modificaCampoSchema` dice
 * «non toccarlo» per ciò che non arriva, quindi rispedire tutto riscriverebbe
 * anche i valori che nessuno ha guardato. È il difetto che quello schema è
 * nato apposta per impedire — sta scritto sopra di lui, in `circolo.ts`.
 *
 * ⚠️ Lo STATO (in servizio / spento) non sta qui: ha già il suo bottone nella
 * riga. Due posti per lo stesso gesto sono due posti da tenere d'accordo.
 */
function Modifica({ campo, chiudi }: { campo: CampoCircolo; chiudi: () => void }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [nome, setNome] = useState(campo.name);
  const [ordine, setOrdine] = useState(String(campo.sortOrder));
  const [superficie, setSuperficie] = useState(campo.surface ?? '');
  /**
   * 🚨 TRE stati e non due: sì · no · **non detto**. `indoor` è nullable, e la
   * tabella infatti mostra «—». Con una casella da spuntare, aprire il pannello
   * e salvare trasformerebbe ogni «non detto» in un **no** — una risposta che
   * nessuno ha dato, scritta senza che nessuno la veda.
   */
  const [coperto, setCoperto] = useState(campo.indoor === null ? '' : campo.indoor ? 'si' : 'no');

  const superficieScelta = superficie.trim() === '' ? null : superficie.trim();
  const copertoScelto = coperto === '' ? null : coperto === 'si';

  const cambiato: Record<string, unknown> = {};
  if (nome.trim() !== campo.name) cambiato.name = nome.trim();
  if (Number(ordine) !== campo.sortOrder) cambiato.sortOrder = Number(ordine);
  if (superficieScelta !== campo.surface) cambiato.surface = superficieScelta;
  if (copertoScelto !== campo.indoor) cambiato.indoor = copertoScelto;
  const niente = Object.keys(cambiato).length === 0;

  const salva = useMutation({
    meta: { fatto: `${campo.name} salvato` },
    mutationFn: () => chiama('PATCH', `/settings/courts/${campo.id}`, cambiato),
    onSuccess: () => {
      rileggi();
      chiudi();
    },
  });

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (!niente) salva.mutate();
      }}
    >
      <div className="riga-campi">
        <Campo etichetta="Nome" required value={nome} onChange={(e) => setNome(e.target.value)} />
        <Campo
          etichetta="Ordine in griglia"
          type="number"
          min={0}
          max={999}
          required
          value={ordine}
          onChange={(e) => setOrdine(e.target.value)}
        />
        <Campo etichetta="Superficie" value={superficie} onChange={(e) => setSuperficie(e.target.value)} />
        <label className="campo">
          <span>Coperto</span>
          <select value={coperto} onChange={(e) => setCoperto(e.target.value)}>
            <option value="">non detto</option>
            <option value="si">sì</option>
            <option value="no">no</option>
          </select>
        </label>
      </div>
      <p className="tenue piccolo">
        L’<strong>ordine</strong> decide dove sta la colonna nel calendario. Per accendere o spegnere il campo
        c’è il bottone nella riga.
      </p>
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending || niente}>
          {salva.isPending ? 'Un attimo…' : 'Salva'}
        </button>
        <button type="button" className="piatto" onClick={chiudi}>
          Annulla
        </button>
      </div>
    </form>
  );
}

function Nuovo(): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [nome, setNome] = useState('');
  const [ordine, setOrdine] = useState('1');
  const [superficie, setSuperficie] = useState('');
  const [coperto, setCoperto] = useState(false);

  const crea = useMutation({
    meta: { fatto: `campo ${nome.trim()} aggiunto` },
    mutationFn: () =>
      chiama('POST', '/settings/courts', {
        name: nome,
        sortOrder: Number(ordine),
        surface: superficie.trim() === '' ? null : superficie,
        indoor: coperto,
      }),
    onSuccess: () => {
      setNome('');
      setSuperficie('');
      rileggi();
    },
  });

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Aggiungi un campo</h3>
      <Campo etichetta="Nome" required value={nome} onChange={(e) => setNome(e.target.value)} />
      <Campo
        etichetta="Ordine in griglia"
        type="number"
        min={0}
        required
        value={ordine}
        onChange={(e) => setOrdine(e.target.value)}
      />
      <Campo etichetta="Superficie" value={superficie} onChange={(e) => setSuperficie(e.target.value)} />
      <label className="campo orizzontale">
        <input type="checkbox" checked={coperto} onChange={(e) => setCoperto(e.target.checked)} />
        <span>Coperto</span>
      </label>
      <Guasto errore={crea.error} />
      <button type="submit" disabled={crea.isPending}>
        {crea.isPending ? 'Un attimo…' : 'Aggiungi'}
      </button>
    </form>
  );
}
